import { AppointmentID } from "./Appointment";
import { CommunicationAgreement } from "./CheckIn";
import { Customer, CustomerID } from "./Customer";
import { COMMUNICATION_CHANNELS, COMMUNICATION_RESULT_STATUSES, CustomerCommunicationID, SMS_GATEWAYS, TwilioMessageSID } from "./CustomerCommunication";
import { InterventionID } from "./Intervention";
import { VehicleTyreReplacementID } from "./Tyre";
import { User, UserID } from "./User";
import { WebhookID } from "./Webhook";

export type CommunicationEventID = number;

export type CommunicationReceiverID = number;

export type CustomerCommunicationInterventionID = number;

export type CustomerCommunicationReplacementID = number;

export type DiagnoseOverviewAgreedResultID = number;

export type DiagnoseOverviewDeclinedResultID = number;

export type DiagnoseOverviewContactResultID = number;

export enum COMMUNICATION_EVENTS {
  OnlineCheckInSent = 1,
  OnlineCheckInOpened = 2,
  OnlineCheckInAnswered = 3,
  DiagnoseOverviewSent = 4,
  DiagnoseOverviewOpened = 5,
  DiagnoseOverviewAnswered = 6,
  DiagnoseOverviewResent = 7,
  RepairOverviewSent = 8,
  RepairOverviewResent = 9,
  RepairOverviewOpened = 10
}

export enum COMMUNICATION_RECEIVER_STATUSES {
  Pending = 1,
  Success = 2,
  Failed = 3
}

export enum COMMUNICATION_RECEIVER_WEBHOOK_STATUSES {
  Unknown = 1,
  Unchanged = 2,
  Updated = 3,
  Deleted = 4
}

export interface CommunicationEvent {
  created_on: string;
  updated_on: string;
  id: CommunicationEventID; // Identifier of the CommunicationEvent
  type: COMMUNICATION_EVENTS; // Event type
  ip: string; // IP from which the Customer Answered
  key_locker_pin_sent: boolean; // When the Type is Answered the Customer may have received a KeyLocker PIN
  user_id: UserID | null; // A User was responsible for this event being created
  customer_communication_id: CustomerCommunicationID; // CustomerCommunication Identifier that this event is related to
  customer_id: CustomerID | null; // A Customer was responsible for this event being created
  customer_name: string; // Name entered by the customer when answering a communication
  customer_signature: string | null; // Customer provided their signature
  is_parking_gate_code_sent: boolean; // Is the parking gate code sent
  is_automated: boolean; // This event was created automatically
  user?: User;
  customer?: Customer;
  receivers?: CommunicationReceiver[];
  appointment_id?: AppointmentID;
  mailgun_message_id?: string;
  intervention_results?: CustomerCommunicationIntervention[];
  replacement_results?: CustomerCommunicationReplacement[];
  agreements?: CommunicationAgreement[];
  diagnose_overview_agreed_results?: DiagnoseOverviewAgreedResult[];
  diagnose_overview_declined_results?: DiagnoseOverviewDeclinedResult[];
  diagnose_overview_contact_results?: DiagnoseOverviewContactResult[];
}

export interface CommunicationReceiver {
  created_on: string;
  updated_on: string;
  id: CommunicationReceiverID; // Identifier of the CommunicationReceiver
  channel: COMMUNICATION_CHANNELS; // Channel the communication gets/is sent over
  destination: string; // Where the communication gets/is sent to
  status: COMMUNICATION_RECEIVER_STATUSES; // Status of the communication
  reason: string; // Reason of the failure of the communication
  sms_gateway_id: SMS_GATEWAYS | null; // ID of the SMS Gateway
  twilio_message_sid: TwilioMessageSID | null; // Identifier from Twilio for this message
  mailgun_message_id: string; // Identifier from Mailgun for this message
  webhook_id: WebhookID | null; // Identifier from the Webhook for this message
  webhook_status: COMMUNICATION_RECEIVER_WEBHOOK_STATUSES | null; // Whether the Webhook of this receiver was updated or deleted after the Communication was sent
  communication_event_id: CommunicationEventID; // Identifier from the CommunicationEvent for this message
  appointment_id?: AppointmentID;
}

export interface CustomerCommunicationIntervention {
  created_on: string;
  updated_on: string;
  id: CustomerCommunicationInterventionID; // Identifier of the communication intervention
  title: string; // Title of the Intervention at the time of this object's creation
  description: string; // Description of the Intervention at the time of this object's creation
  price: number; // Price of the Intervention at the time of this object's creation
  vat: number | null; // VAT of the Intervention at the time of this object's creation
  communication_result_status: COMMUNICATION_RESULT_STATUSES; // What the customer decided for this intervention
  intervention_id: InterventionID | null; // Identifier of the Intervention that the Customer saw
  communication_event_id: CommunicationEventID; // Identifier of the answered CommunicationEvent
  appointment_id?: AppointmentID; // Frontend need this to filter when receiving the WS message for this model
}

export interface CustomerCommunicationReplacement {
  created_on: string;
  updated_on: string;
  id: CustomerCommunicationReplacementID; // Identifier of the communication replacement
  ean: string; // EAN of the replacement at the time of this object's creation
  manufacturer: string; // Manufacturer of the replacement at the time of this object's creation
  reference: string; // Reference of the replacement at the time of this object's creation
  price: number; // Price of the replacement at the time of this object's creation
  vat: number | null; // VAT of the replacement at the time of this object's creation
  communication_result_status: COMMUNICATION_RESULT_STATUSES; // What the customer decided for this replacement
  vehicle_tyre_replacement_id: VehicleTyreReplacementID | null; // Identifier of the replacement
  communication_event_id: CommunicationEventID; // Identifier of the answered CommunicationEvent
  appointment_id?: AppointmentID; // Frontend need this to filter when receiving the WS message for this model
}

export interface DiagnoseOverviewAgreedResult {
  created_on: string;
  id: DiagnoseOverviewAgreedResultID; // Identifier of the agreed diagnose overview result
  title: string; // Title of the result at the time of this object's creation
  mechanic_notes: string; // Notes of the mechanic
  price: number; // Price of the result at the time of this object's creation
  vat: number | null; // VAT of the result at the time of this object's creation
  communication_event_id: CommunicationEventID; // Identifier of the answered CommunicationEvent
  appointment_id?: AppointmentID;
}

export interface DiagnoseOverviewDeclinedResult {
  created_on: string;
  id: DiagnoseOverviewDeclinedResultID; // Identifier of the declined diagnose overview result
  title: string; // Title of the result at the time of this object's creation
  mechanic_notes: string; // Notes of the mechanic
  price: number; // Price of the result at the time of this object's creation
  vat: number | null; // VAT of the result at the time of this object's creation
  communication_event_id: CommunicationEventID; // Identifier of the answered CommunicationEvent
  appointment_id?: AppointmentID;
}

export interface DiagnoseOverviewContactResult {
  created_on: string;
  id: DiagnoseOverviewContactResultID; // Identifier of the contact me diagnose overview result
  title: string; // Title of the result at the time of this object's creation
  mechanic_notes: string; // Notes of the mechanic
  price: number; // Price of the result at the time of this object's creation
  vat: number | null; // VAT of the result at the time of this object's creation
  communication_event_id: CommunicationEventID; // Identifier of the answered CommunicationEvent
  appointment_id?: AppointmentID;
}
